import {classNames, React, useLocalStore, observer, useAsObservableSource, useContext, useCallback, pluralize, Fragment} from '@api'
import {AnchorButton, Button, Popover, Icon} from '@blueprintjs/core'
import {Add} from '@material-ui/icons'
import {GroupByGroupStore, useGroupByGroupStore, BuilderCardWithPopoverContainer, useCardFieldStore, BuilderCardLoader, BuilderCardFieldStore, NestedMiniBuilderCard, NestedCardItemContextMenu, CardFieldStoreContext, BuilderCardStoreContext} from '@modules/catalog'
import {CatalogItemDetailsStore, FieldRendererContext, FieldRendererStore} from '@design-board/stores'
import {IContextMenuItems} from '@modules/contextMenus'
import {ISidebarDetailsStore, useCatalogItemSidebarDetailsStore} from '@modules'
import {ICatalogFieldConfig, ui, DetailsViewFieldConfig, StoresInstance, WorkspaceConfig} from '@stores'
import {ListPreviewCard, PrimaryIconButton} from 'centric-ui-components'
import {useVipLocalStore, VipLocalStore} from '@modules'
import {computed, action, observable} from 'mobx'
import * as css from './NestedCardItems.css'
import {SidebarSpecification} from '@lazy'
import { Tooltip } from '@material-ui/core'
import { Popover2, Tooltip2 } from '@api'

//import { Tooltip } from '@material-ui/core'

interface MyProps {
  field?: ICatalogFieldConfig,
  items?: Array<any>
  parent?: any
  levelOfDetail?: number
  className?: string,
  disableSelection?: boolean
  workspace?: Partial<WorkspaceConfig>,
}

/// region Store
export const NestedCardItemsContext = React.createContext<NestedCardItemsStore>(undefined)
export const useNestedCardItemsStore = () => useContext(NestedCardItemsContext)

export class NestedCardItemsStore extends VipLocalStore implements IContextMenuItems {
  constructor(stores: StoresInstance,private _props: MyProps, public groupStore: GroupByGroupStore, public cardFieldStore: BuilderCardFieldStore) {
    super(stores)
  }

  get nestedDragHandlers() { return this.cardFieldStore.nestedDragHandlers }

  @computed get props() {
    const {_props} = this,
      // Fix for bug on 5/19/21 with server writing arrays instead of scalars  value can be {value} even when reflist (should be array of length 1).  Value can be [[1], [2], [3], ...] due to server bug
      items = !_.isArray(_props.items) ? [_props.items] : _props.items.map(v => _.isArray(v) ? _.last(v) : v).filter(_.identity)

    return {..._props, items}
  }

  get contextMenuItems() {
    const {cardFieldStore, cardFieldStore: {cardStore}} = this
    return (
      <BuilderCardStoreContext.Provider value={cardStore}>
        <CardFieldStoreContext.Provider value={cardFieldStore}>
          <NestedCardItemsContext.Provider value={this}>
            <NestedCardItemContextMenu/>
          </NestedCardItemsContext.Provider>
        </CardFieldStoreContext.Provider>
      </BuilderCardStoreContext.Provider>
    )
  }

  @computed get lod() {return this.props.levelOfDetail ?? 0}

  @computed get prefixField() { return `${this.props.field.field}.`}

  @computed get visibleCardFields(): Array<ICatalogFieldConfig> {
    const {catalog, props: {field}, prefixField} = this
    return catalog.cv.visibleCardFields.filter(f => f.field != field.field && f.field.startsWith(prefixField)).map(f => ({...f, field: f.field.substring(field.field.length + 1)}))
  }

  @computed get library() { return this.workspaces.librariesOfCurrentWorkspace.find(l => l.index == this.props.field.esIndex)}

  @computed get filteredItems() {
    const {prefixField, groupStore, props: {items}, filter} = this
    return filter.filterNested(prefixField, _.isArray(items) ? items : items ? [items] : items, groupStore).filter(f => f && (f._id || f.id || f.exId))
  }

  @observable showAll = false
  @action onClickShowAll = () => { this.showAll = true }

  @computed get childProps() {
    const {props: {field, className, levelOfDetail, parent, disableSelection, ...rest}, library, visibleCardFields, cardFieldStore} = this
    return {
      className: css.card,
      imageSource: 'images',
      nested: {parentField: cardFieldStore?.cardStore?.props?.nested?.field, parent: parent ?? cardFieldStore?.cardStore?.data, field},
      library,
      visibleCardFields,
      type: field.esIndex,
      field,
      ...rest
    }
  }

  @observable.ref hoverItem = null

  @observable isNestedMenuOpen: boolean = false

  @action toggleAddMenu = () => {
    this.isNestedMenuOpen = !this.isNestedMenuOpen
  }

  @action onAddMenuOpen = () => {
    this.isNestedMenuOpen = true
  }

  @action onCloseMenuOpen = () => {
    this.isNestedMenuOpen = false
  }

  nestedSwatchEvents = (item) => ({
    onMouseEnter: e => {
      this.hoverItem = item
    }
  })

  @computed get viewMode() {
    return this.cardFieldStore.nestedItemsAsList ? 'list' : 'cards'
  }

  @computed get style() {
    const {cardFieldStore: {associatedWorkspace}} = this

    return {
      '--image-source-object-fit': associatedWorkspace?.catalog?.features?.card?.objectFit ?? 'cover'
    } as any
  }

  onContextMenu = e => {
    this.contextMenu.onComponentContextMenu(e, this)
  }

  @action onMouseLeave = () => {
   /*this.hoverItem = null*/  // Don't instantly remove the item otherwise our popover will have nothing to render while it waits/animates to close
  }
}

/// endregion

/// region Components

/**
 * Something like a list of concept colors, materials, shapes etc in a field in a thing like a Theme
 * Usage is like:
 <BuilderCard>
   <CardField>
     <NestedCardItems>
   </Fragment>
 </Fragment>

 However we want to show a single popover that's shared between all the individual cards for further details on the items in the list
 So our tree is:

 <BuilderCard>
 <CardField>
  <NestedCardItems>   // Only Creates a store and passes it
    <NestedPopover /> or <NestedCardPopoverContent />
 </Fragment>
 */
export const NestedCardItems = observer<MyProps>(function NestedCardItems(props) {
  const {field: {card}} = props,
    obsProps = useAsObservableSource(props),
    groupStore = useGroupByGroupStore(),
    cardFieldStore = useCardFieldStore(),
    store = useVipLocalStore((ws) => new NestedCardItemsStore(ws, obsProps, groupStore, cardFieldStore))

  return (
    <NestedCardItemsContext.Provider value={store}>
      {card?.nested?.showNestedPopover ? <NestedCards {...props}/> : <NestedPopover {...props}/>}
    </NestedCardItemsContext.Provider>
  )
})

const NestedPopover = observer<MyProps>(function NestedPopover(props) {
  const {settings: {cardPopover: {ms, closeMs}}} = ui,
    {hoverItem, isNestedMenuOpen} = useNestedCardItemsStore()
  return (
    <Popover2 position='right-top'
             autoFocus={false}
             captureDismiss={false}
             enforceFocus={false}
             usePortal
             lazy
             disabled={isNestedMenuOpen || props?.field?.renderer === "text"}
             interactionKind='hover'
             hoverOpenDelay={ms}
             hoverCloseDelay={closeMs}
             className={css.nestedPopover}
             modifiers={{
               preventOverflow: {enabled: false},
               hide: {enabled: false},
               flip: {enabled: false},
               arrow: {enabled: hoverItem != null}
             }}
             openOnTargetFocus={false}
             content={<NestedPopoverContent/>}>
      <NestedCards {...props}/>
    </Popover2>)
})

export const NestedPopoverContent = observer(function NestedPopoverContent() {
  const {hoverItem, catalog} = useNestedCardItemsStore()
  return hoverItem ? <BuilderCardLoader
    hideButtons
    isPopover
    levelOfDetail={1}
    nested={{field: hoverItem.field, data: hoverItem.data}}
    id={catalog.idFor(hoverItem.data)}
    //image={hoverItem.image}
  /> : null
})

/**
 * A list of children items
 */
const NestedCards = observer(function NestedCards() {
  const store = useNestedCardItemsStore(),
    {strings, onCloseMenuOpen, isNestedMenuOpen, showAll, onClickShowAll, lod, filteredItems, props: {workspace, className, field}, catalog, cardFieldStore, viewMode, onContextMenu, style, onMouseLeave} = store,
    detailsStore: ISidebarDetailsStore = useCatalogItemSidebarDetailsStore() ?? {index: workspace.catalog.index ?? catalog.index, isSearching: false, adoptionStore: null},
    obsRendererStoreProps = useAsObservableSource({field: field as DetailsViewFieldConfig}),
    fieldRendererStore = useVipLocalStore((ws) => new FieldRendererStore(ws, obsRendererStoreProps, {detailsStore, cardFieldStore}))

  //logJS({field, filteredItems})
  return <FieldRendererContext.Provider value={fieldRendererStore}>
    <NestedCardItemsContext.Provider value={store}>
      <SidebarSpecification
        className={classNames(css.addButtonSkeletonPopover)}
        popoverPosition={'right'}
        isPopoverOpen={isNestedMenuOpen}
        field={field}
        data={store.props.items}
        onClosePopover={onCloseMenuOpen}
        >
      <div className={classNames(className, css.root, css.grid)}
         data-type={'NestedCardList'}
         data-view={viewMode}
         data-renderer={field?.renderer}
         onContextMenu={onContextMenu}
         data-length={filteredItems?.length}
         style={style}
         onMouseLeave={onMouseLeave}
         data-lod={lod}>
      {filteredItems?.length
       ? <Fragment>
         {/* The individual linked entities (colorways, styles, etc) */}
         {_.take(filteredItems, showAll ? filteredItems.length : ui.settings.maxNestedItemsToShow)
           .map((data, i) => <NestedCardItem className={css.item} key={i} data={data} i={i}/>)
         }
         {filteredItems.length > ui.settings.maxNestedItemsToShow && !showAll &&
		  <AnchorButton
			  minimal
			  className={css.showAll}
			  text={`Show All\n(${filteredItems.length})`}
			  onClick={onClickShowAll}
		  />}
       </Fragment>
       : field.card?.nested?.add || cardFieldStore.editable ? null : strings.emptyLabel}
      <NestedCardItemsAddButtonSkeleton2/>
    </div>
      </SidebarSpecification>
    </NestedCardItemsContext.Provider>
  </FieldRendererContext.Provider>
})


const NestedCardItem = observer(function NestedCardItem({className, data, i} : {className?: string, data: any, i: number}) {
  if (_.isArray(data)) {
    data = _.first(data)
  }

  //logJS(data)
  const store = useNestedCardItemsStore(),
    {props: {field: {card}}, lod, childProps, catalog, contextMenu, stores} = store,
    id = catalog.idFor(data),
    onContextMenu = useCallback((e) => contextMenu.onComponentContextMenu(e, store, {data}), [store, data])

  return (card?.nested?.renderer == 'inline' || card?.nested?.renderer == 'ListPreviewCard'
       ? <ListPreviewCard data-id={id} stores={stores}
                          onContextMenu={onContextMenu}
                          data={data}
                          className={className}
                          cardDetails={card?.nested?.cardDetails}/>
       : card?.nested?.showNestedPopover ?
         <BuilderCardWithPopoverContainer
           key={id}
           className={classNames(css.card, className)}
           item={data}
           onContextMenu={onContextMenu}
           extraCardProps={{
             ...childProps,
             levelOfDetail: lod,
             disableSelection: true,
             nested: {...childProps.nested, arrayIndex: i, data}
           }}
         /> : <NestedMiniBuilderCard
           key={catalog.idFor(data)}
           className={className}
           onContextMenu={onContextMenu}
           data={data}
         />)
})

const NestedCardItemsAddButton = observer(function NestedCardItemsAddButton() {
    const store = useNestedCardItemsStore(),
      {isNestedMenuOpen, catalog, props: {parent, field, field: {card}}, cardFieldStore: {editable, nestedItemsAsList}} = store

  return (card?.nested?.add || editable) && (
    <SidebarSpecification
           className={classNames(css.specNested, {[css.nestedItemAsListAdd]: nestedItemsAsList})}
           popoverPosition={'right'}
           field={field}
           button={
             <Tooltip2 content={isNestedMenuOpen ? '' : `Add ${catalog.labelForField(field)}`} placement='right'>
               <div data-type='NestedCardItemsRenderer'>
                 {<PrimaryIconButton className={css.addMore} data-type='AddMoreButton' data-open={isNestedMenuOpen} icon={<Icon icon='add'/>}
                                     text={nestedItemsAsList && `Add ${catalog.labelForFieldName_currentCatalog(field.field, 'singular')}`}/>}
               </div>
             </Tooltip2>}/>
  )
})

const NestedCardItemsAddButtonSkeleton = observer(function NestedCardItemsAddButtonSkeleton() {
  const store = useNestedCardItemsStore(),
    {isNestedMenuOpen, catalog, onAddMenuOpen, onCloseMenuOpen, props: {field, items, field: {card}}, cardFieldStore: {editable}} = store

  return (card?.nested?.add || editable) && (
    <SidebarSpecification
      className={classNames(css.addButtonSkeletonPopover)}
      popoverPosition={'right'}
      field={field}
      data={items}
      onOpenPopover={onAddMenuOpen}
      onClosePopover={onCloseMenuOpen}
      button={
        <Tooltip2 content={isNestedMenuOpen ? '' : `Add ${pluralize(catalog.labelForField(field), 1)}`} placement='bottom'>
          <Button minimal className={css.addMore} data-type='AddMoreButton' data-open={isNestedMenuOpen} icon={<Add/>}
                  //onMouseEnter={() => store.hoverItem = null}  // Don't do this, it'll instantly collapse the popover
                  //text={nestedItemsAsList && `Add ${catalog.labelForFieldName_currentCatalog(field.field, 'singular')}`}
          />
        </Tooltip2>}
    />
  )
})

const NestedCardItemsAddButtonSkeleton2 = observer(function NestedCardItemsAddButtonSkeleton() {
  const store = useNestedCardItemsStore(),
    {isNestedMenuOpen, catalog, toggleAddMenu, onAddMenuOpen, onCloseMenuOpen, props: {field, items, field: {card}}, cardFieldStore: {editable}} = store

  return (card?.nested?.add || editable) && (
    <div className={css.addButtonSkeletonPopover}>
        <Tooltip2 content={isNestedMenuOpen ? '' : `Add ${pluralize(catalog.labelForField(field), 1)}`} placement='bottom'>
          <Button minimal className={css.addMore} data-type='AddMoreButton' data-open={isNestedMenuOpen} onClick={toggleAddMenu} icon={<Add/>}
            //onMouseEnter={() => store.hoverItem = null}  // Don't do this, it'll instantly collapse the popover
            //text={nestedItemsAsList && `Add ${catalog.labelForFieldName_currentCatalog(field.field, 'singular')}`}
          />
        </Tooltip2>
    </div>)
})



// WEBPACK FOOTER //
// ./src/modules/catalog/product/productCard/components/BuilderCard/NestedCardItems/NestedCardItems.tsx